import Link from 'next/link'
import { useTranslations } from 'next-intl'

import classNames from 'classnames'

import BlockContent from '~/components/atoms/BlockContent/BlockContent'
import CollapsibleReadMore from '~/components/atoms/CollapsibleReadMore/CollapsibleReadMore'
import Container from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import { Icon } from '~/components/atoms/Icon/Icon'
import Image from '~/components/atoms/Image/Image'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'

import styles from './TeamProfileHeader.module.css'

export interface TeamProfileHeaderProps {
  _id?: string
  name: string
  magazineRole: string[]
  jobTitle: string
  phoneNumber: string
  email: string
  linkedIn: string
  description: any
  image: any
  articleCount: number
  pressArticleCount: number
  theme?: ComponentTheme
  testID?: string
  featuredLogos?: {
    image: any
    link: string
  }[]
}

export default function TeamProfileHeader({
  _id,
  name,
  magazineRole,
  jobTitle,
  phoneNumber,
  email,
  linkedIn,
  description,
  image,
  articleCount,
  pressArticleCount,
  theme = ComponentTheme.primary,
  featuredLogos,
  testID,
}: TeamProfileHeaderProps) {
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMedium})`)
  const t = useTranslations('common')

  return (
    <Fixture theme={theme} id="profile">
      <Container>
        <div className={styles.base} data-testid={testID}>
          {/* 50% Grid */}
          <div className={styles.headerContainer}>
            {/* Profile image and Bio Container */}
            <div className={styles.profileInfo}>
              {/* Image */}
              <div className={styles.imageContainer}>
                {image?.asset && (
                  <Image
                    asset={image.asset}
                    objectFit="initial"
                    sizes="(max-width: 799px) 100vw, 50vw"
                    alt={image.alt}
                    className={classNames(styles.image)}
                  />
                )}
              </div>
              {/* Bio Container */}
              <div className={styles.bioContainer}>
                <Text
                  Tag="h2"
                  size={TextSizes.magSecondaryTitleLrg}
                  className={styles.name}
                >
                  {name}
                </Text>
                <Text
                  Tag="p"
                  size={TextSizes.primaryTitleSml}
                  className={styles.jobTitle}
                >
                  {jobTitle}
                </Text>
                <div className={styles.contactBioContainer}>
                  <div className={styles.contactDetails}>
                    <Link href={`tel:${phoneNumber}`} target="_blank">
                      <Text
                        Tag="p"
                        size={TextSizes.bodySml}
                        className={styles.phoneNumber}
                      >
                        {phoneNumber}
                      </Text>
                    </Link>
                    <Link href={`mailto:${email}`} target="_blank">
                      <Text
                        Tag="p"
                        size={TextSizes.bodySml}
                        className={styles.email}
                      >
                        {email}
                      </Text>
                    </Link>
                    {linkedIn && (
                      <Link
                        href={linkedIn}
                        target="_blank"
                        className={styles.linkedIn}
                      >
                        <Icon type="LinkedIn" />
                      </Link>
                    )}
                  </div>
                  <div className={styles.headerBioText}>
                    <div className={styles.descriptionContainer}>
                      <CollapsibleReadMore
                        theme={ComponentTheme.secondary}
                        maxHeight={isMobile ? 130 : 46}
                        readMoreText={t('readMore')}
                        boldReadMoreText
                        onlyMobile={false}
                        uppercaseReadMoreText
                      >
                        <Text
                          size={TextSizes.bodyMed}
                          className={styles.description}
                        >
                          {description && <BlockContent value={description} />}
                        </Text>
                      </CollapsibleReadMore>
                    </div>
                    {featuredLogos && featuredLogos.length > 0 && (
                      <div className={styles.featuredLogosContainer}>
                        {featuredLogos.map((logo, index) => (
                          <div key={index} className={styles.featuredLogoItem}>
                            <Link href={logo.link || '#'} target="_blank">
                              <Image
                                asset={logo.image.asset}
                                objectFit="contain"
                                sizes="(max-width: 799px) 100vw, 50vw"
                                alt={logo.image.alt}
                                className={classNames(styles.featuredLogo)}
                              />
                            </Link>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Fixture>
  )
}
